import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonCopy } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStandard } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { assign, map, partial } from "lodash"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  currentUserId: string
  standards: Array<CardStandard>
  cardStackId: string
}

export default function lessonStandardsCopyAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_STANDARDS_COPY",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("user", payload.currentUserId).set("attributes.clipboard.card", {
    type: "card-standards",
    attributes: { standards: payload.standards },
  }).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let context = defaultLessonContext(payload, models)
  let standards = map(payload.standards, (standard) => {
    return standard.code
  })
  let standardSets = map(payload.standards, (standard) => {
    return `${standard.jurisdiction} - ${standard.grades} - ${standard.subject}`
  })
  let newContext = assign({}, context, { standards, standardSets })

  let newEvent: LessonCopy = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "copy",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "standards",

    adverbial_verb: null,
    adverbial_object_type: null,
    adverbial_object_specifier: null,
    adverbial_object_id: null,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: "Lesson Standard Copied",
    titleForAnalytics: "Standards - copy",
    description: `Copied from ${standardSets}`,
    context: newContext,
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
